import { PARAMETRES_PHOTO, type Encodeur, type PhotoBrute } from './photo.js';
import { RefusDuServeur, type MetaTransfert, type StockageLocal, type TransportTransfert } from './transfert.js';

/**
 * Adaptateurs du navigateur pour M05 : stockage de l'appareil (IndexedDB), envoi par morceaux (protocole tus 1.0 sur l'adresse
 * rendue par `portail.pieces.deposer`), lecture de la photo choisie et encodeur canvas. Chaque adaptateur a un repli sûr quand
 * le navigateur n'offre pas l'API attendue.
 */

/** Stockage en mémoire de la page : repli quand IndexedDB est absent ou refusé (navigation privée). */
export function stockageMemoire(): StockageLocal {
  const donnees = new Map<string, unknown>();
  return {
    async lire<T>(cle: string) {
      return donnees.has(cle) ? (structuredClone(donnees.get(cle)) as T) : undefined;
    },
    async ecrire(cle, valeur) {
      donnees.set(cle, structuredClone(valeur));
    },
    async supprimer(cle) {
      donnees.delete(cle);
    },
    async cles(prefixe = '') {
      return [...donnees.keys()].filter((c) => c.startsWith(prefixe)).sort();
    },
    async vider() {
      donnees.clear();
    },
  };
}

const BASE = 'lakou-documents';
const MAGASIN = 'appareil';

const requete = <T>(r: IDBRequest<T>): Promise<T> =>
  new Promise((resoudre, rejeter) => {
    r.onsuccess = () => resoudre(r.result);
    r.onerror = () => rejeter(r.error ?? new Error('IndexedDB'));
  });

/**
 * Stockage de l'appareil : IndexedDB quand le navigateur l'offre, sinon la mémoire de la page. Si la base ne s'ouvre pas
 * (navigation privée, espace refusé), le stockage bascule sur la mémoire de la page au lieu d'échouer.
 */
export function stockageNavigateur(fabrique: IDBFactory | undefined = globalThis.indexedDB): StockageLocal {
  const repli = stockageMemoire();
  if (fabrique === undefined) return repli;
  let base: Promise<IDBDatabase | null> | undefined;
  const ouvrir = (): Promise<IDBDatabase | null> => {
    base ??= new Promise((resoudre) => {
      const r = fabrique.open(BASE, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(MAGASIN);
      r.onsuccess = () => resoudre(r.result);
      r.onerror = () => resoudre(null);
    });
    return base;
  };
  const avec = async <T>(mode: IDBTransactionMode, idb: (m: IDBObjectStore) => Promise<T>, memoire: () => Promise<T>): Promise<T> => {
    const db = await ouvrir();
    return db === null ? memoire() : idb(db.transaction(MAGASIN, mode).objectStore(MAGASIN));
  };
  return {
    lire: <T>(cle: string) =>
      avec(
        'readonly',
        async (m) => (await requete(m.get(cle))) as T | undefined,
        () => repli.lire<T>(cle),
      ),
    ecrire: (cle, valeur) =>
      avec(
        'readwrite',
        async (m) => void (await requete(m.put(valeur, cle))),
        () => repli.ecrire(cle, valeur),
      ),
    supprimer: (cle) =>
      avec(
        'readwrite',
        async (m) => void (await requete(m.delete(cle))),
        () => repli.supprimer(cle),
      ),
    cles: (prefixe = '') =>
      avec(
        'readonly',
        async (m) =>
          (await requete(m.getAllKeys()))
            .map(String)
            .filter((c) => c.startsWith(prefixe))
            .sort(),
        () => repli.cles(prefixe),
      ),
    vider: () =>
      avec(
        'readwrite',
        async (m) => void (await requete(m.clear())),
        () => repli.vider(),
      ),
  };
}

const base64 = (texte: string): string => btoa(String.fromCodePoint(...new TextEncoder().encode(texte)));

/**
 * Transport tus 1.0 vers l'adresse de la session du portail. Création idempotente par la clé de l'envoi (le serveur rend le
 * décalage déjà acquis), morceaux envoyés au décalage exact, empreinte SHA-256 en métadonnée. Un refus d'empreinte (460)
 * vaut « corrompu » ; toute autre réponse en échec lève, et l'envoi est repris plus tard.
 */
/** Réponse « hors ligne » fabriquée par le service worker quand la requête n'a pas pu partir. */
async function horsLigne(r: Response): Promise<boolean> {
  try {
    return ((await r.clone().json()) as { code?: unknown } | null)?.code === 'HORS_LIGNE';
  } catch {
    return false;
  }
}

export function transportTus(uploadUrl: string, fetchFn: typeof fetch = (...a) => globalThis.fetch(...a)): TransportTransfert {
  const tailles = new Map<string, number>();
  const offset = (r: Response): number => Number(r.headers.get('Upload-Offset') ?? '0');
  const verifier = async (r: Response, attendu: string): Promise<Response> => {
    // Une panne du serveur (5xx) n'est pas une coupure : l'envoi s'arrête au lieu d'attendre le réseau (S2 du 27/10). Le 503
    // « HORS_LIGNE » du service worker (sw/strategies.ts) en est une, lui : il reste une coupure.
    if (r.status >= 500 && !(r.status === 503 && (await horsLigne(r)))) throw new RefusDuServeur(r.status);
    if (!r.ok) throw new Error(`${attendu} : réponse ${r.status}`);
    return r;
  };
  return {
    async creer(meta: MetaTransfert) {
      const r = await verifier(
        await fetchFn(uploadUrl, {
          method: 'POST',
          headers: {
            'Tus-Resumable': '1.0.0',
            'Upload-Length': String(meta.taille),
            'Upload-Metadata': `filename ${base64(meta.nom)},filetype ${base64(meta.type)},sha256 ${base64(meta.empreinteSha256)}`,
            'Idempotency-Key': meta.cle,
          },
        }),
        'Création de l’envoi',
      );
      const id = r.headers.get('Location') ?? uploadUrl;
      tailles.set(id, meta.taille);
      return { id, decalage: offset(r) };
    },
    async decalage(id) {
      return offset(await verifier(await fetchFn(id, { method: 'HEAD', headers: { 'Tus-Resumable': '1.0.0' } }), 'Décalage'));
    },
    async envoyer(id, decalage, octets) {
      const r = await fetchFn(id, {
        method: 'PATCH',
        headers: {
          'Tus-Resumable': '1.0.0',
          'Upload-Offset': String(decalage),
          'Content-Type': 'application/offset+octet-stream',
        },
        body: new Uint8Array(octets),
      });
      if (r.status === 460) return { decalage: decalage + octets.length, etat: 'corrompu' as const };
      const acquis = offset(await verifier(r, 'Morceau'));
      return {
        decalage: acquis,
        etat: acquis >= (tailles.get(id) ?? Number.POSITIVE_INFINITY) ? ('termine' as const) : ('en_cours' as const),
      };
    },
  };
}

/** Lit la photo choisie : ses octets, son type, et ses dimensions quand le navigateur sait les lire. */
export async function lirePhoto(
  fichier: File,
  lireDimensions: ((f: Blob) => Promise<{ width: number; height: number }>) | undefined = globalThis.createImageBitmap,
): Promise<PhotoBrute & { nom: string }> {
  const octets = new Uint8Array(await fichier.arrayBuffer());
  let largeur: number = PARAMETRES_PHOTO.largeurMax;
  let hauteur = 0;
  if (lireDimensions !== undefined) {
    try {
      ({ width: largeur, height: hauteur } = await lireDimensions(fichier));
    } catch {
      // Dimensions illisibles : la largeur maximale de l'encodage s'applique.
    }
  }
  return { nom: fichier.name || 'photo.jpg', mime: fichier.type || 'image/jpeg', octets, largeur, hauteur };
}

/** Encodeur canvas : réduit l'image à `largeurMax` et la réencode en JPEG à la qualité demandée. Sans canvas, rend la photo telle quelle. */
export const encodeurCanvas: Encodeur = async (entree, { largeurMax, qualite }) => {
  if (typeof createImageBitmap !== 'function' || typeof OffscreenCanvas !== 'function') return entree;
  const image = await createImageBitmap(new Blob([new Uint8Array(entree)]));
  const echelle = Math.min(1, largeurMax / image.width);
  const toile = new OffscreenCanvas(Math.round(image.width * echelle), Math.round(image.height * echelle));
  toile.getContext('2d')?.drawImage(image, 0, 0, toile.width, toile.height);
  const blob = await toile.convertToBlob({ type: 'image/jpeg', quality: qualite });
  return new Uint8Array(await blob.arrayBuffer());
};
